/**
 * Το πλέγμα: γραμμές = είδη της αλυσίδας, στήλες = καταστήματα (§13.5, §13.11).
 *
 * ΤΟ ΠΛΕΓΜΑ ΕΙΝΑΙ ΤΟ ΠΡΟΪΟΝ. Η δουλειά που καταργεί («τι ακριβώς διαφέρει πού»)
 * γίνεται μόνο αν ο χειριστής βλέπει 22 καταστήματα δίπλα-δίπλα, γι' αυτό:
 *  • virtualization ΚΑΙ ΣΤΟΥΣ ΔΥΟ άξονες (useGridViewport) — ~5.700 κελιά δεν
 *    μπαίνουν στο DOM,
 *  • παγωμένες αριστερές στήλες με `position: sticky; left: 0` μέσα σε κάθε
 *    γραμμή· χωρίς αυτές, στο 12ο κατάστημα δεν ξέρεις ποιο είδος κοιτάς,
 *  • επεξεργασία inline με Enter = επόμενη γραμμή, Esc = ακύρωση — η ζεστή
 *    διαδρομή είναι «ίδια αλλαγή σε πολλά είδη στη σειρά».
 *
 * Καμία νέα εξάρτηση npm: το package.json δεν έχει βιβλιοθήκη πλέγματος και δεν
 * μπαίνει μία για σταθερό ύψος γραμμής και σταθερό πλάτος στήλης.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { GridCellV5, GridRow, GridStore } from '../../services/platformCatalogApi';
import {
  CELL_STATE_LABELS, cellIsEditable, COL_AVAIL_ALL_W, COL_CATALOG_W, COL_NAME_W, COL_PRICE_ALL_W,
  COL_SELECT_W, fmtDelta, fmtMoney, FROZEN_W, hasPendingDelete, HEADER_H, OVERRIDE_MODE_LABELS,
  parsePriceInput, PRICE_REASON_LABELS, PriceCellInfo, PriceIndex, priceAllLabel, priceAllPreview,
  ROW_H, shownAvailable, shownPrice, storeShort, STORE_COL_W,
} from './catalogUi';
import { useGridViewport, visibleRange } from './useGridViewport';

type Line =
  | { kind: 'cat'; key: string; label: string; count: number; collapsed: boolean }
  | { kind: 'row'; key: string; row: GridRow };

/**
 * Ποιο κελί επεξεργάζεται. Το `masterKey` ΔΕΝ είναι διακοσμητικό: ο δείκτης
 * γραμμής δεν είναι ταυτότητα (μια σύμπτυξη κατηγορίας ή νέα σελίδα τον
 * μετακινεί), και χωρίς αυτόν δεν μπορούμε να ξεχωρίσουμε «η γραμμή μου έφυγε»
 * από «οι γραμμές ξαναήρθαν με άλλη ταυτότητα πίνακα».
 */
interface EditingCell { line: number; storeId: number; masterKey: string }

interface Props {
  rows: GridRow[];
  stores: GridStore[];
  loading: boolean;
  canEdit: boolean;
  /** Ύψος της περιοχής κύλισης σε px. */
  height: number;
  collapsed: Record<string, boolean>;
  onToggleCategory: (label: string) => void;
  selectedRows: Record<string, boolean>;
  selectedStores: Record<string, boolean>;
  onToggleRow: (masterKey: string) => void;
  onToggleStore: (storeId: number) => void;
  onToggleAllRows: (value: boolean) => void;
  onSetPrice: (row: GridRow, storeId: number, cell: GridCellV5, price: number) => void;
  onSetAvailability: (row: GridRow, storeId: number, cell: GridCellV5, value: boolean) => void;
  onSetPriceAll: (row: GridRow, price: number) => void;
  onSetAvailabilityAll: (row: GridRow, value: boolean) => void;
  /**
   * Φ5: η σύγκριση με τον κατάλογο (delivery). `null` όσο δεν έχει φορτωθεί —
   * η στήλη δείχνει «—» και ΔΕΝ υποκρίνεται ότι «συμφωνούν όλα».
   */
  priceIndex?: PriceIndex | null;
  /** «Να μην υπάρχει στο efood» (§13.19.Γ) — μόνο σε κατάσταση επεξεργασίας. */
  canDelete?: boolean;
  onDeleteRow?: (row: GridRow) => void;
}

/** Ένα κελί καταστήματος. Κρατιέται μικρό: ζωγραφίζεται ~500 φορές ανά scroll. */
const StoreCell: React.FC<{
  row: GridRow;
  storeId: number;
  cell: GridCellV5 | undefined;
  canEdit: boolean;
  editing: boolean;
  draft: string;
  onDraft: (v: string) => void;
  onStartEdit: () => void;
  /** Enter: αποθήκευση και μετακίνηση στην επόμενη γραμμή. */
  onCommitNext: () => void;
  /** Blur: αποθήκευση ΧΩΡΙΣ μετακίνηση, μόνο αν επεξεργαζόμαστε ακόμη ΑΥΤΟ το κελί. */
  onCommitHere: () => void;
  onCancel: () => void;
  onToggleAvailability: () => void;
  /** Η σύγκριση ΑΥΤΟΥ του κελιού με τον κατάλογο (Φ5) — undefined = καμία διαφορά/άγνωστο. */
  price?: PriceCellInfo;
}> = ({
  row, storeId, cell, canEdit, editing, draft, onDraft, onStartEdit,
  onCommitNext, onCommitHere, onCancel, onToggleAvailability, price: catalogPrice,
}) => {
  if (!cell || cell.state === 'missing') {
    return (
      <div
        className="h-full flex items-center justify-center text-t4 text-xs"
        title="Λείπει από αυτό το κατάστημα — δεν παράγεται αλλαγή"
      >
        —
      </div>
    );
  }

  if (cell.state === 'gone') {
    return (
      <div
        className="h-full flex flex-col items-center justify-center text-t4 text-[11px] leading-tight"
        title={`Έφυγε από τον κατάλογο του efood${cell.missing_since ? ` στις ${new Date(cell.missing_since).toLocaleDateString('el-GR')}` : ''}`}
      >
        <span>—</span>
        <span className="opacity-70">έφυγε</span>
      </div>
    );
  }

  const price = shownPrice(cell);
  const available = shownAvailable(cell);
  // ΟΛΕΣ οι εκκρεμείς αλλαγές του κελιού: ένα κελί μπορεί να έχει ταυτόχρονα
  // πρόχειρη τιμή ΚΑΙ πρόχειρη διαθεσιμότητα — και οι δύο πρέπει να φαίνονται,
  // αλλιώς ο χειριστής νομίζει ότι η μία χάθηκε ενώ θα σταλεί κανονικά.
  const pendingList = (cell.pending_edits && cell.pending_edits.length > 0)
    ? cell.pending_edits
    : (cell.pending ? [cell.pending] : []);
  const pendingPriceEdit = pendingList.filter(p => p && p.field === 'price')[0] || null;
  const pendingPrice = !!pendingPriceEdit;
  const pendingAvail = pendingList.some(p => p && p.field === 'is_available');
  const conflict = cell.state === 'conflict';
  const twin = cell.twin && cell.twin.members > 1 ? cell.twin : null;
  const structureBad = cell.structure_ok === false;
  const markedForDelete = hasPendingDelete(cell);
  // Η κουκκίδα διαφοράς μπαίνει ΜΟΝΟ όταν δεν υπάρχει ήδη πρόχειρη τιμή: εκεί ο
  // χειριστής έχει ήδη απαντήσει στην ερώτηση «τι θα γίνει με αυτή την τιμή», και
  // δύο σήματα στο ίδιο 76άρι κελί διαβάζονται ως ένα.
  const deltaBadge = (!pendingPrice && catalogPrice && catalogPrice.delta !== null)
    ? catalogPrice : null;

  const cellTitle = [
    `${row.display_name} · κατ. ${storeId} · ${cell.item_code || ''}`,
    `Κατάσταση: ${CELL_STATE_LABELS[cell.state] || cell.state}`,
    price !== null ? `Τιμή: ${fmtMoney(price)}` : '',
    catalogPrice && catalogPrice.catalog !== null
      ? `Τιμή καταλόγου (delivery): ${fmtMoney(catalogPrice.catalog)}`
        + (catalogPrice.delta !== null ? ` · διαφορά ${fmtDelta(catalogPrice.delta)}` : '')
        + (catalogPrice.rows > 1 ? ` · ${catalogPrice.rows} γραμμές (είδος + μεγέθη)` : '')
        + `\nΑπόφαση: ${OVERRIDE_MODE_LABELS[catalogPrice.mode]}`
      : '',
    markedForDelete ? 'ΣΗΜΕΙΩΜΕΝΟ ΓΙΑ ΔΙΑΓΡΑΦΗ από το efood (φεύγει με την αποστολή/μετάπτωση)' : '',
    available === null ? '' : (available ? 'Διαθέσιμο' : 'Μη διαθέσιμο'),
    twin ? `Δίδυμο τιμής: ${twin.members} αντίγραφα (${twin.role === 'offer_shadow' ? 'είδος προσφοράς' : 'κύριο'})`
      + `${twin.prices_agree ? ' — ίδια τιμή σε όλα' : ' — ΔΙΑΦΟΡΕΤΙΚΕΣ τιμές ήδη, δεν αλλάζουν μαζί'}`
      + (twin.siblings && twin.siblings.length ? `\nΑδέλφια: ${twin.siblings.join(', ')}` : '') : '',
    structureBad ? 'Διαφορετική δομή από την επικρατούσα — οι αλλαγές δομής είναι φραγμένες εδώ' : '',
    cell.in_offer_category ? 'Ανήκει σε κατηγορία προσφοράς — η διαθεσιμότητα αλλάζει τι βλέπει ο πελάτης' : '',
    conflict ? 'ΣΥΓΚΡΟΥΣΗ: η τιμή άλλαξε στο efood — χρειάζεται επαναφόρτωση' : '',
  ].filter(Boolean).join('\n');

  return (
    <div
      className={`h-full px-1 flex flex-col justify-center gap-0.5 ${conflict ? 'bg-red-500/10' : ''} ${
        markedForDelete ? 'bg-red-500/[0.14] ring-1 ring-inset ring-red-500/60' : ''}`}
      title={cellTitle}
    >
      {editing ? (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={e => onDraft(e.target.value)}
          onBlur={onCommitHere}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); onCommitNext(); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          className="w-full bg-surface-0 border border-blue-500 rounded px-1 py-0.5 text-[12px] text-right text-t1"
        />
      ) : (
        <button
          type="button"
          disabled={!canEdit}
          onClick={onStartEdit}
          className={`w-full text-right text-[12px] leading-tight rounded px-1 tabular-nums
            ${pendingPrice ? 'text-amber-700 dark:text-amber-300 font-semibold' : 'text-t1'}
            ${markedForDelete ? 'line-through text-red-700 dark:text-red-300' : ''}
            ${canEdit ? 'hover:bg-blue-500/10' : 'cursor-default'}`}
        >
          {pendingPriceEdit && (
            <span className="text-[10px] text-t4 line-through mr-0.5">{fmtMoney(pendingPriceEdit.from as any)}</span>
          )}
          {/* Η διαφορά από τον κατάλογο (delivery). Μπλε = «κράτα την τιμή
              efood» (απόφαση χειριστή, δεν στέλνεται ποτέ), πορτοκαλί = θα
              σταλεί αν τρέξει συγχρονισμός τιμών. */}
          {deltaBadge && (
            <span className={`text-[9px] leading-none px-0.5 rounded mr-0.5 align-middle ${
              deltaBadge.mode === 'keep_platform'
                ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300'
                : 'bg-amber-500/25 text-amber-800 dark:text-amber-200'}`}>
              {fmtDelta(deltaBadge.delta)}
            </span>
          )}
          {price === null ? '—' : fmtMoney(price)}
        </button>
      )}

      <div className="flex items-center justify-between gap-0.5 px-0.5">
        <button
          type="button"
          disabled={!canEdit}
          onClick={onToggleAvailability}
          title={available === null ? 'Άγνωστη διαθεσιμότητα' : (available ? 'Διαθέσιμο — κλικ για κλείσιμο' : 'Μη διαθέσιμο — κλικ για άνοιγμα')}
          className={`w-3 h-3 rounded-full shrink-0 ${canEdit ? '' : 'cursor-default'}
            ${available === null ? 'bg-slate-400' : available ? 'bg-emerald-500' : 'bg-red-500'}
            ${pendingAvail ? 'ring-2 ring-amber-500' : ''}`}
        />
        <span className="flex items-center gap-0.5">
          {structureBad && <AlertTriangle className="w-3 h-3 text-amber-500" />}
          {cell.in_offer_category && (
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500" title="Είδος κατηγορίας προσφοράς" />
          )}
          {twin && (
            <span
              className={`text-[10px] leading-none px-0.5 rounded ${
                twin.prices_agree
                  ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300'
                  : 'bg-amber-500/25 text-amber-800 dark:text-amber-200'}`}
            >
              ×{twin.members}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const CatalogGrid: React.FC<Props> = ({
  rows, stores, loading, canEdit, height, collapsed, onToggleCategory,
  selectedRows, selectedStores, onToggleRow, onToggleStore, onToggleAllRows,
  onSetPrice, onSetAvailability, onSetPriceAll, onSetAvailabilityAll,
  priceIndex = null, canDelete = false, onDeleteRow,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { viewport, onScroll } = useGridViewport(scrollRef);

  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [draft, setDraft] = useState('');
  const [editingAll, setEditingAll] = useState<string | null>(null);
  const [draftAll, setDraftAll] = useState('');

  /** Επίπεδη λίστα: κεφαλίδα κατηγορίας + οι γραμμές της (όσες δεν είναι κλειστές). */
  const lines = useMemo<Line[]>(() => {
    const out: Line[] = [];
    const order: string[] = [];
    const byCat: Record<string, GridRow[]> = {};
    for (let i = 0; i < rows.length; i++) {
      const label = rows[i].category_label || 'Χωρίς κατηγορία';
      if (!byCat[label]) { byCat[label] = []; order.push(label); }
      byCat[label].push(rows[i]);
    }
    for (let i = 0; i < order.length; i++) {
      const label = order[i];
      const isCollapsed = !!collapsed[label];
      out.push({ kind: 'cat', key: `cat:${label}`, label, count: byCat[label].length, collapsed: isCollapsed });
      if (isCollapsed) continue;
      for (let j = 0; j < byCat[label].length; j++) {
        out.push({ kind: 'row', key: byCat[label][j].master_key, row: byCat[label][j] });
      }
    }
    return out;
  }, [rows, collapsed]);

  const totalWidth = FROZEN_W + stores.length * STORE_COL_W;
  // Χωρίς γραμμές κρατάμε λίγο ύψος για το μήνυμα κενού, αλλιώς το μήνυμα
  // ξεχειλίζει από δοχείο ύψους μηδέν και φαίνεται μόνο με κύλιση.
  const bodyHeight = lines.length > 0 ? lines.length * ROW_H : 140;

  const rowRange = visibleRange(viewport.scrollTop, Math.max(0, viewport.height - HEADER_H), ROW_H, lines.length, 6);
  const colRange = visibleRange(viewport.scrollLeft, Math.max(0, viewport.width - FROZEN_W), STORE_COL_W, stores.length, 2);

  /** Φέρνει τη γραμμή μέσα στο ορατό παράθυρο (Enter = επόμενη γραμμή). */
  const ensureLineVisible = useCallback((lineIndex: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const top = lineIndex * ROW_H;
    const viewTop = el.scrollTop;
    const viewBottom = el.scrollTop + el.clientHeight - HEADER_H;
    if (top < viewTop) el.scrollTop = top;
    else if (top + ROW_H > viewBottom) el.scrollTop = top + ROW_H - (el.clientHeight - HEADER_H);
  }, []);

  const startEdit = useCallback((lineIndex: number, storeId: number) => {
    const line = lines[lineIndex];
    if (!line || line.kind !== 'row') return;
    const cell = line.row.cells ? line.row.cells[String(storeId)] : undefined;
    if (!cellIsEditable(cell)) return;
    const price = shownPrice(cell);
    setEditing({ line: lineIndex, storeId, masterKey: line.row.master_key });
    setDraft(price === null ? '' : fmtMoney(price));
  }, [lines]);

  const commitEdit = useCallback((moveNext: boolean) => {
    const current = editing;
    if (!current) return;
    const line = lines[current.line];
    if (line && line.kind === 'row') {
      const cell = line.row.cells ? line.row.cells[String(current.storeId)] : undefined;
      const next = parsePriceInput(draft);
      const before = shownPrice(cell);
      // Ίδια τιμή = καμία αλλαγή. Χωρίς αυτόν τον έλεγχο, ένα σκέτο Enter πάνω σε
      // 22 κελιά θα γεννούσε 22 άχρηστες γραμμές edit.
      if (cell && next !== null && (before === null || Math.abs(next - before) > 0.0001)) {
        onSetPrice(line.row, current.storeId, cell, next);
      }
    }
    if (!moveNext) { setEditing(null); setDraft(''); return; }
    for (let i = current.line + 1; i < lines.length; i++) {
      const candidate = lines[i];
      if (candidate.kind !== 'row') continue;
      const cell = candidate.row.cells ? candidate.row.cells[String(current.storeId)] : undefined;
      if (!cellIsEditable(cell)) continue;
      ensureLineVisible(i);
      setEditing({ line: i, storeId: current.storeId, masterKey: candidate.row.master_key });
      setDraft(shownPrice(cell) === null ? '' : fmtMoney(shownPrice(cell)));
      return;
    }
    setEditing(null);
    setDraft('');
  }, [editing, draft, lines, onSetPrice, ensureLineVisible]);

  /**
   * Blur: αποθηκεύει ΜΟΝΟ αν επεξεργαζόμαστε ακόμη το ίδιο κελί.
   *
   * ΓΙΑΤΙ ο έλεγχος ταυτότητας: με Enter η επεξεργασία έχει ήδη μετακινηθεί στην
   * επόμενη γραμμή· ένα καθυστερημένο blur του παλιού input χωρίς αυτόν τον
   * έλεγχο θα «περνούσε» την τιμή στο νέο κελί και θα πηδούσε δύο γραμμές.
   */
  const commitFromBlur = useCallback((lineIndex: number, storeId: number) => {
    if (!editing || editing.line !== lineIndex || editing.storeId !== storeId) return;
    commitEdit(false);
  }, [editing, commitEdit]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setDraft('');
  }, []);

  /**
   * Το ίδιο πρόβλημα ταυτότητας με το `commitFromBlur`, αλλά για τη στήλη «Τιμή
   * (όλα)»: με Escape το input ΞΕΣΤΗΝΕΤΑΙ, και ο browser μπορεί να στείλει blur
   * στο στοιχείο που φεύγει. Χωρίς τον έλεγχο, ένα Escape θα εφάρμοζε την τιμή
   * σε ΟΛΑ τα καταστήματα — δηλαδή θα δημιουργούσε έως 22 γραμμές edit (συν
   * δίδυμα) ακριβώς τη στιγμή που ο χειριστής ζήτησε ακύρωση.
   */
  const editingAllRef = useRef<string | null>(null);
  editingAllRef.current = editingAll;
  const draftAllRef = useRef('');
  draftAllRef.current = draftAll;

  const commitAllFromBlur = useCallback((row: GridRow) => {
    if (editingAllRef.current !== row.master_key) return;
    const value = parsePriceInput(draftAllRef.current);
    setEditingAll(null);
    setDraftAll('');
    if (value !== null) onSetPriceAll(row, value);
  }, [onSetPriceAll]);

  const cancelEditAll = useCallback(() => {
    setEditingAll(null);
    setDraftAll('');
  }, []);

  /**
   * Κλείνει το ανοιχτό input ΜΟΝΟ όταν χαθεί η γραμμή που επεξεργαζόταν.
   *
   * ΓΙΑΤΙ ΟΧΙ σκέτο «άλλαξαν τα rows»: κάθε αποθήκευση αλλαγής επιστρέφει ΝΕΟ
   * πίνακα γραμμών (applyEditsToRows). Με εξάρτηση στην ταυτότητα του πίνακα, το
   * Enter («αποθήκευσε και πήγαινε στην επόμενη γραμμή») αυτοκτονούσε: ~200ms
   * μετά το πάτημα ερχόταν η απάντηση, το input της ΕΠΟΜΕΝΗΣ γραμμής ξεστηνόταν
   * και ό,τι είχε ήδη πληκτρολογηθεί χανόταν — δηλαδή έσπαγε η ζεστή διαδρομή
   * του §13.11 σε κάθε γραμμή.
   */
  useEffect(() => {
    setEditing(prev => {
      if (!prev) return prev;
      const line = lines[prev.line];
      return (line && line.kind === 'row' && line.row.master_key === prev.masterKey) ? prev : null;
    });
    setEditingAll(prev => {
      if (!prev) return prev;
      for (let i = 0; i < rows.length; i++) if (rows[i].master_key === prev) return prev;
      return null;
    });
  }, [lines, rows]);

  const allRowsSelected = rows.length > 0 && rows.every(r => !!selectedRows[r.master_key]);

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="relative overflow-auto border border-line rounded-xl bg-surface-2"
      style={{ height }}
    >
      <div style={{ width: totalWidth, height: HEADER_H + bodyHeight, position: 'relative' }}>
        {/* ── Κεφαλίδα ── */}
        <div
          className="sticky top-0 flex bg-surface-1 border-b border-line-strong"
          style={{ height: HEADER_H, width: totalWidth, zIndex: 3 }}
        >
          <div
            className="sticky left-0 flex items-stretch shrink-0 bg-surface-1 border-r border-line-strong"
            style={{ width: FROZEN_W, zIndex: 4 }}
          >
            <div className="flex items-center justify-center shrink-0" style={{ width: COL_SELECT_W }}>
              <input
                type="checkbox"
                checked={allRowsSelected}
                onChange={e => onToggleAllRows(e.target.checked)}
                title="Επιλογή όλων των ορατών γραμμών"
                className="w-4 h-4 rounded"
              />
            </div>
            <div className="flex items-center px-2 text-[11px] uppercase text-t3 shrink-0 overflow-hidden" style={{ width: COL_NAME_W }}>
              Είδος
            </div>
            <div className="flex items-center px-2 text-[11px] uppercase text-t3 shrink-0 overflow-hidden" style={{ width: COL_PRICE_ALL_W }}>
              Τιμή (όλα)
            </div>
            {/* Φ5 (§13.19.Α): τι ΘΑ ΕΠΡΕΠΕ να κοστίζει σύμφωνα με τον κατάλογό
                μας — κανάλι delivery, ΧΩΡΙΣ τέλος πλαστικού (η τιμή delivery το
                περιέχει ήδη· μετρήθηκε στις 17/9). */}
            <div
              className="flex items-center px-2 text-[11px] uppercase text-t3 shrink-0 overflow-hidden"
              style={{ width: COL_CATALOG_W }}
              title={'Τιμή καταλόγου (κανάλι delivery), αυτούσια — ΠΟΤΕ + τέλος πλαστικού.\n'
                + 'Στα είδη με μεγέθη δείχνει το εύρος των τιμών των μεγεθών.'}
            >
              Κατάλογος
            </div>
            <div
              className="flex items-center px-2 text-[11px] uppercase text-t3 shrink-0 overflow-hidden"
              style={{ width: COL_AVAIL_ALL_W }}
              title="Διαθεσιμότητα σε όλα τα καταστήματα"
            >
              Διαθ. (όλα)
            </div>
          </div>

          <div className="shrink-0" style={{ width: colRange.start * STORE_COL_W }} />
          {stores.slice(colRange.start, colRange.end).map(s => (
            <div
              key={s.store_id}
              className="flex flex-col items-center justify-center gap-0.5 border-l border-line px-0.5 shrink-0 overflow-hidden"
              style={{ width: STORE_COL_W }}
              title={`${s.store_name}${s.imported_at ? `\nΕισαγωγή: ${new Date(s.imported_at).toLocaleString('el-GR')}` : ''}`}
            >
              <input
                type="checkbox"
                checked={!!selectedStores[String(s.store_id)]}
                onChange={() => onToggleStore(s.store_id)}
                className="w-3.5 h-3.5 rounded"
                title="Επιλογή στήλης για μαζική ενέργεια"
              />
              <span className="text-[11px] text-t2 font-medium truncate w-full text-center">
                {storeShort(s.store_name, s.short)}
              </span>
            </div>
          ))}
        </div>

        {/* ── Γραμμές ── */}
        {lines.slice(rowRange.start, rowRange.end).map((line, idx) => {
          const lineIndex = rowRange.start + idx;
          const top = HEADER_H + lineIndex * ROW_H;

          if (line.kind === 'cat') {
            return (
              <div
                key={line.key}
                className="absolute flex bg-surface-1/80 border-b border-line"
                style={{ top, left: 0, height: ROW_H, width: totalWidth }}
              >
                <div
                  className="sticky left-0 flex items-center gap-1.5 px-2 shrink-0 bg-surface-1 border-r border-line-strong"
                  style={{ width: FROZEN_W, zIndex: 2 }}
                >
                  <button
                    type="button"
                    onClick={() => onToggleCategory(line.label)}
                    className="flex items-center gap-1.5 text-sm text-t1 font-medium min-w-0"
                  >
                    {line.collapsed ? <ChevronRight className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
                    <span className="truncate">{line.label}</span>
                    <span className="text-xs text-t3 shrink-0">{line.count}</span>
                  </button>
                </div>
              </div>
            );
          }

          const row = line.row;
          const selected = !!selectedRows[row.master_key];
          const editingAllHere = editingAll === row.master_key;

          // Προ-μέτρηση της «Τιμή (όλα)», με ΤΟΥΣ ΙΔΙΟΥΣ κανόνες που θα
          // εφαρμοστούν (catalogUi.priceAllPreview).
          const allPreview = editingAllHere ? priceAllPreview(row, stores, parsePriceInput(draftAll)) : null;
          const allPreviewLabel = !allPreview ? ''
            : allPreview.stores === 0
              ? 'καμία αλλαγή'
              : `${allPreview.stores} κατ.${allPreview.twins > 0 ? ` · ${allPreview.twins} δίδ.` : ''}`;
          const allPreviewTitle = !allPreview ? ''
            : allPreview.stores === 0
              ? 'Κανένα κατάστημα δεν αλλάζει με αυτή την τιμή'
              : `Θα δημιουργηθούν ${allPreview.stores} γραμμές αλλαγής`
                + (allPreview.twins > 0 ? ` και θα παρασυρθούν ${allPreview.twins} δίδυμα τιμής` : '');

          const catalogInfo = priceIndex ? priceIndex.byMaster[row.master_key] : undefined;
          const catalogTitle = !catalogInfo ? '' : [
            `Τιμή καταλόγου (delivery): ${catalogInfo.catalog_min === null ? '—' : fmtMoney(catalogInfo.catalog_min)}`
              + (catalogInfo.catalog_max !== null && catalogInfo.catalog_max !== catalogInfo.catalog_min
                ? ` – ${fmtMoney(catalogInfo.catalog_max)} (μεγέθη)` : ''),
            catalogInfo.delta !== null
              ? `Μεγαλύτερη διαφορά efood − κατάλογος: ${fmtDelta(catalogInfo.delta)} σε ${catalogInfo.stores} καταστήματα`
              : 'Καμία διαφορά',
            `Απόφαση: ${OVERRIDE_MODE_LABELS[catalogInfo.mode]}`,
            catalogInfo.note ? `Σημείωση: ${catalogInfo.note}` : '',
            catalogInfo.wouldSend > 0
              ? `Ο συγχρονισμός τιμών θα φτιάξει ${catalogInfo.wouldSend} αλλαγές για αυτό το είδος`
              : 'Ο συγχρονισμός τιμών ΔΕΝ αγγίζει αυτό το είδος',
            catalogInfo.reason ? `Χωρίς τιμή καταλόγου: ${PRICE_REASON_LABELS[catalogInfo.reason]}` : '',
          ].filter(Boolean).join('\n');

          return (
            <div
              key={line.key}
              className={`absolute flex border-b border-line ${selected ? 'bg-blue-500/[0.06]' : 'bg-surface-2'}`}
              style={{ top, left: 0, height: ROW_H, width: totalWidth }}
            >
              {/* Παγωμένες αριστερές στήλες */}
              {/* Το παγωμένο μπλοκ πρέπει να έχει ΑΔΙΑΦΑΝΟ φόντο: κυλά από πάνω
                  του ολόκληρο το πλέγμα και οποιαδήποτε διαφάνεια δείχνει τις
                  στήλες των καταστημάτων να περνούν μέσα από το όνομα. */}
              <div
                className={`sticky left-0 flex items-stretch shrink-0 border-r border-line-strong ${selected ? 'bg-blue-50 dark:bg-slate-700' : 'bg-surface-2'}`}
                style={{ width: FROZEN_W, zIndex: 2 }}
              >
                <div className="flex items-center justify-center shrink-0" style={{ width: COL_SELECT_W }}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleRow(row.master_key)}
                    className="w-4 h-4 rounded"
                  />
                </div>

                <div className="flex flex-col justify-center px-2 min-w-0 shrink-0" style={{ width: COL_NAME_W }}>
                  <span className="text-[13px] text-t1 truncate" title={`${row.display_name}\n${row.master_key}`}>
                    {row.display_name}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-t4 min-w-0">
                    <span className="truncate min-w-0">{row.category_label}</span>
                    {row.stores_present <= 1 && (
                      <span className="px-1 rounded bg-surface-3 text-t3 shrink-0" title="Υπάρχει μόνο σε ένα κατάστημα">τοπικό</span>
                    )}
                    {row.is_offer_master && (
                      <span className="px-1 rounded bg-violet-500/20 text-violet-700 dark:text-violet-300 shrink-0" title="Είδος κατηγορίας προσφοράς">προσφορά</span>
                    )}
                    {row.divergent_structure_count > 0 && (
                      <span className="px-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0"
                        title={`${row.divergent_structure_count} καταστήματα με διαφορετική δομή`}>≠ δομή</span>
                    )}
                    <span className="shrink-0 ml-auto flex items-center gap-1">
                      {/* «Να μην υπάρχει στο efood» (§13.19.Γ): πρόχειρη αλλαγή
                          `field='delete'`, ρίσκο E. ΔΕΝ σβήνει τίποτα τώρα —
                          πριν τη μετάπτωση το είδος απλώς δεν μπαίνει στο
                          payload, μετά φεύγει με DELETE /catalog. */}
                      {canDelete && onDeleteRow && (
                        <button
                          type="button"
                          onClick={() => onDeleteRow(row)}
                          title={'Να μην υπάρχει στο efood: φτιάχνει πρόχειρη αλλαγή διαγραφής.\n'
                            + 'Η γραμμή αντιστοίχισης ονόματος ΔΕΝ πειράζεται.'}
                          className="p-0.5 rounded text-t4 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      <span title="Σε πόσα καταστήματα υπάρχει">
                        {row.stores_present}/{row.stores_total}
                      </span>
                    </span>
                  </span>
                </div>

                {/* «Τιμή (όλα)» — γράψιμο εδώ φτιάχνει ΜΙΑ αλλαγή ανά κατάστημα
                    που όντως διαφέρει (§13.5): δεν υπάρχει «κεντρική τιμή». */}
                <div className="flex items-center px-1.5 shrink-0" style={{ width: COL_PRICE_ALL_W }}>
                  {editingAllHere ? (
                    <input
                      autoFocus
                      type="text"
                      inputMode="decimal"
                      value={draftAll}
                      onChange={e => setDraftAll(e.target.value)}
                      onBlur={() => commitAllFromBlur(row)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancelEditAll(); }
                      }}
                      className="w-full bg-surface-0 border border-blue-500 rounded px-1 py-0.5 text-xs text-right text-t1"
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => { setEditingAll(row.master_key); setDraftAll(''); }}
                      title={canEdit
                        ? 'Ορισμός τιμής σε ΟΛΑ τα καταστήματα που την έχουν διαφορετική'
                        : 'Μόνο ανάγνωση'}
                      className={`w-full text-right text-xs tabular-nums rounded px-1 py-1
                        ${row.price_distinct > 1 ? 'text-amber-700 dark:text-amber-300' : 'text-t2'}
                        ${canEdit ? 'hover:bg-blue-500/10' : 'cursor-default'}`}
                    >
                      {priceAllLabel(row)}
                    </button>
                  )}
                </div>

                {/* «Κατάλογος (delivery)» — Φ5. Η στήλη ΔΕΝ είναι επεξεργάσιμη:
                    η τιμή έρχεται από τον κατάλογό μας, και η μόνη ανθρώπινη
                    απόφαση είναι η «Απόφαση» της υπο-καρτέλας «Τιμές». */}
                <div className="flex flex-col justify-center px-1.5 shrink-0 overflow-hidden" style={{ width: COL_CATALOG_W }}>
                  {!priceIndex ? (
                    <span className="text-[11px] text-t4" title="Η σύγκριση με τον κατάλογο δεν έχει φορτωθεί ακόμη">—</span>
                  ) : catalogInfo ? (
                    <>
                      <span className="text-[12px] text-t1 tabular-nums truncate" title={catalogTitle}>
                        {catalogInfo.catalog_min === null
                          ? '—'
                          : (catalogInfo.catalog_max !== null && catalogInfo.catalog_max !== catalogInfo.catalog_min
                            ? `${fmtMoney(catalogInfo.catalog_min)}–${fmtMoney(catalogInfo.catalog_max)}`
                            : fmtMoney(catalogInfo.catalog_min))}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] leading-none">
                        {catalogInfo.delta !== null && (
                          <span className={`px-1 rounded ${
                            catalogInfo.mode === 'keep_platform'
                              ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300'
                              : 'bg-amber-500/25 text-amber-800 dark:text-amber-200'}`}>
                            {fmtDelta(catalogInfo.delta)}
                          </span>
                        )}
                        {catalogInfo.stores > 0 && (
                          <span className="text-t4" title={`Σε ${catalogInfo.stores} καταστήματα η τιμή του efood διαφέρει`}>
                            ≠{catalogInfo.stores}
                          </span>
                        )}
                        {catalogInfo.mode !== 'catalog' && (
                          <span className="text-t4 truncate" title={OVERRIDE_MODE_LABELS[catalogInfo.mode]}>
                            {catalogInfo.mode === 'keep_platform' ? 'κράτα efood' : 'σταθερή'}
                          </span>
                        )}
                      </span>
                    </>
                  ) : (
                    <span
                      className="text-[11px] text-t4"
                      title={priceIndex.complete
                        ? 'Καμία διαφορά με τον κατάλογο σε κανένα κατάστημα.'
                        : 'Δεν αναφέρθηκε διαφορά, αλλά η σύγκριση ΔΕΝ ήταν πλήρης (φίλτρο «μόνο όσα διαφέρουν», κομμένη λίστα ή σύγκριση ΕΝΟΣ καταστήματος). Δεν ξέρουμε αν το είδος συγκρίθηκε καν.'}
                    >
                      {priceIndex.complete ? 'ίδια' : '—'}
                    </span>
                  )}
                </div>

                {/* «Διαθεσιμότητα (όλα)» — και, ΟΣΟ γράφεται τιμή στο «Τιμή
                    (όλα)», η προ-μέτρηση αυτής της ενέργειας (§13.11: πόσες
                    γραμμές και πόσα δίδυμα ΠΡΙΝ εφαρμοστεί). Μπαίνει εδώ και όχι
                    σε αιωρούμενο πλακίδιο επειδή το πλέγμα είναι virtualized: ό,τι
                    βγαίνει έξω από το ύψος της γραμμής το σκεπάζει η επόμενη. */}
                <div className="flex items-center justify-between gap-1 px-1.5 shrink-0 overflow-hidden" style={{ width: COL_AVAIL_ALL_W }}>
                  {editingAllHere ? (
                    <span className="text-[11px] text-blue-600 dark:text-blue-400 truncate" title={allPreviewTitle}>
                      {allPreviewLabel}
                    </span>
                  ) : (
                    <>
                      <span
                        className={`text-[11px] ${row.unavailable_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-t4'}`}
                        title="Σε πόσα καταστήματα είναι κλειστό"
                      >
                        {row.unavailable_count > 0 ? `${row.unavailable_count} κλειστά` : 'όλα ανοιχτά'}
                      </span>
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={!canEdit}
                          onClick={() => onSetAvailabilityAll(row, true)}
                          title="Διαθέσιμο σε όλα τα καταστήματα"
                          className="w-3.5 h-3.5 rounded-full bg-emerald-500 disabled:opacity-40"
                        />
                        <button
                          type="button"
                          disabled={!canEdit}
                          onClick={() => onSetAvailabilityAll(row, false)}
                          title="Μη διαθέσιμο σε όλα τα καταστήματα"
                          className="w-3.5 h-3.5 rounded-full bg-red-500 disabled:opacity-40"
                        />
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Στήλες καταστημάτων (μόνο οι ορατές) */}
              <div className="shrink-0" style={{ width: colRange.start * STORE_COL_W }} />
              {stores.slice(colRange.start, colRange.end).map(s => {
                const cell = row.cells ? row.cells[String(s.store_id)] : undefined;
                const isEditing = !!editing && editing.line === lineIndex && editing.storeId === s.store_id;
                return (
                  <div
                    key={s.store_id}
                    className={`border-l border-line shrink-0 overflow-hidden ${selectedStores[String(s.store_id)] ? 'bg-blue-500/[0.05]' : ''}`}
                    style={{ width: STORE_COL_W }}
                  >
                    <StoreCell
                      row={row}
                      storeId={s.store_id}
                      cell={cell}
                      canEdit={canEdit && cellIsEditable(cell)}
                      editing={isEditing}
                      draft={draft}
                      onDraft={setDraft}
                      onStartEdit={() => startEdit(lineIndex, s.store_id)}
                      onCommitNext={() => commitEdit(true)}
                      onCommitHere={() => commitFromBlur(lineIndex, s.store_id)}
                      onCancel={cancelEdit}
                      price={priceIndex ? priceIndex.byCell[`${row.master_key}|${s.store_id}`] : undefined}
                      onToggleAvailability={() => {
                        if (!cell) return;
                        const current = shownAvailable(cell);
                        onSetAvailability(row, s.store_id, cell, !(current === true));
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}

        {lines.length === 0 && (
          <div
            className="absolute left-0 flex items-center justify-center w-full text-sm text-t4"
            style={{ top: HEADER_H, height: 120 }}
          >
            {loading
              ? <Loader2 className="w-5 h-5 animate-spin" />
              : 'Καμία γραμμή με αυτά τα φίλτρα. Κάνε πρώτα εισαγωγή από το efood.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogGrid;
